/**
 * Les langues de BUL, et rien d'autre.
 *
 * Ce fichier ne dépend de personne : il se lit depuis le module i18n comme
 * depuis un écran, sans rien charger de i18next. C'est la seule liste de
 * langues de l'application — un ajout se fait ici, et le reste suit.
 *
 * Trois langues, et ce n'est pas un hasard : le malgache est la langue de la
 * rue, le français celle des papiers, l'anglais celle des acheteurs venus
 * d'ailleurs. Une boutique d'Antananarivo doit pouvoir travailler dans la
 * première sans jamais lire les deux autres.
 */

/** Les codes de langue, dans l'ordre où le sélecteur les propose. */
export const LOCALES = ['fr', 'mg', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * Le français par défaut.
 *
 * C'est la langue dans laquelle l'application a été écrite, donc la seule dont
 * on soit certain qu'aucune clé n'y manque : c'est aussi pour cela qu'elle sert
 * de repli quand une traduction n'a pas suivi.
 */
export const DEFAULT_LOCALE: Locale = 'fr';

/**
 * Le nom d'une langue s'écrit toujours dans cette langue.
 *
 * Un sélecteur qui traduirait ses propres entrées serait illisible pour la
 * personne qui le cherche : quelqu'un qui ne lit que le malgache doit
 * reconnaître « Malagasy » dans une liste affichée en anglais. C'est pour cela
 * que ces libellés ne sont pas des traductions et ne vivent pas dans les
 * fichiers de messages.
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  fr: 'Français',
  mg: 'Malagasy',
  en: 'English',
};

/** L'énuméré du serveur, porté par `UserProfile.language`. */
export type ApiLanguage = 'FR' | 'MG' | 'EN';

const API_LANGUAGE_BY_LOCALE: Record<Locale, ApiLanguage> = {
  fr: 'FR',
  mg: 'MG',
  en: 'EN',
};

const LOCALE_BY_API_LANGUAGE: Record<ApiLanguage, Locale> = {
  FR: 'fr',
  MG: 'mg',
  EN: 'en',
};

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as ReadonlyArray<string>).includes(value);

/** Ce que le profil enregistre côté serveur. */
export const toApiLanguage = (locale: Locale): ApiLanguage => API_LANGUAGE_BY_LOCALE[locale];

/** Ce que le profil rend, ou `null` si le serveur parle d'une langue qu'on n'a pas. */
export function fromApiLanguage(language: string | null | undefined): Locale | null {
  if (typeof language !== 'string') {
    return null;
  }
  return LOCALE_BY_API_LANGUAGE[language.toUpperCase() as ApiLanguage] ?? null;
}

/**
 * La langue de l'appareil, ramenée à l'une des nôtres.
 *
 * Un code régional (`fr-CA`, `en-GB`) se réduit à sa langue : on ne distingue
 * pas les variantes, et refuser `fr-CA` pour ouvrir en français par défaut
 * reviendrait au même à un détour près.
 */
export function matchLocale(tag: string | null | undefined): Locale | null {
  if (typeof tag !== 'string' || tag === '') {
    return null;
  }
  const language = tag.toLowerCase().split(/[-_]/)[0];
  return isLocale(language) ? language : null;
}

/**
 * L'étiquette BCP 47 complète, pour ce qui sort de l'application.
 *
 * Seul l'en-tête `Accept-Language` s'en sert aujourd'hui. Les formats, eux, ne
 * passent jamais par `Intl` : voir `format.ts`.
 */
export const BCP47: Record<Locale, string> = {
  fr: 'fr-MG',
  mg: 'mg-MG',
  en: 'en-US',
};
